import { createUseStyles } from 'react-jss';
import { IonButton, IonButtons, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/react';
import { PredefinedColors } from '@ionic/core';
import { isPlatform } from '../utils/isPlatform';
import { MenuCloseButton } from '../components/MenuCloseButton';
import { MenuBackButton } from './MenuBackButton';
import { useBreakpoints } from '../theme/useBreakpoints';
import { IThemeColor } from '../theme/IThemeColor';
import { MinusIcon } from '../theme/Icons';

type IStyleKeys = 'modal' | 'modalHeader';
interface IStyleProps { desktopMaxHeight?: number; desktopMinHeight?: number; desktopMaxWidth?: number; isAboveMd: boolean; };

const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    modal: {
        '--max-width': ({ desktopMaxWidth, isAboveMd }) => (isPlatform('desktop') || isAboveMd) && desktopMaxWidth ? `${desktopMaxWidth}px` : undefined,
        '--max-height': ({ desktopMaxHeight, isAboveMd }) => (isPlatform('desktop') || isAboveMd) && desktopMaxHeight ? `${desktopMaxHeight}px` : undefined,
        '--min-height': ({ desktopMinHeight, isAboveMd }) => (isPlatform('desktop') || isAboveMd) && desktopMinHeight ? `${desktopMinHeight}px` : undefined,

        // Bump Ionic's default centered-card height (600px) to 700px, at the same breakpoint where
        // Ionic switches to that presentation. Scoped to this shared Modal so it never overrides
        // modals that size themselves (bottom sheets, full-screen overlays) -- a global ion-modal
        // rule did exactly that and clipped the Sharing & Access sheet's footer.
        '@media (min-width: 768px) and (min-height: 768px)': {
            '--height': '700px'
        }
    },

    modalHeader: {
        '& ion-title': {
            paddingInlineStart: 16
        }
    }
});

type IModalProps = {
    'data-id'?: string;
    'data-type'?: string;
    title: string;
    children: React.ReactNode;
    isOpen: boolean;
    backHandler?: () => void;
    closeHandler: () => void;
    minimizeHandler?: () => void;
    onWillPresent?: () => void;
    onDidPresent?: () => void;
    stackedModal?: boolean;
    keepContentsMounted?: boolean;
    desktopMaxWidth?: number;
    desktopMaxHeight?: number;
    desktopMinHeight?: number;
    maximizeDesktopHeight?: boolean;
    excludeAutoDismiss?: boolean;
    preventBackdropDismiss?: boolean;
    hideCloseButton?: boolean;
    centerTitle?: boolean;
    titleColor?: IThemeColor;
    buttonColor?: PredefinedColors;
};

export const Modal: React.FC<IModalProps> = ({
    'data-id': dataId,
    'data-type': dataType,
    title,
    children,
    isOpen = false,
    backHandler,
    closeHandler,
    minimizeHandler,
    onWillPresent = () => {},
    onDidPresent = () => {},
    stackedModal = false,
    keepContentsMounted,
    desktopMaxWidth,
    desktopMaxHeight,
    desktopMinHeight,
    maximizeDesktopHeight = false,
    preventBackdropDismiss = false,
    hideCloseButton = false,
    centerTitle,
    titleColor,
    buttonColor,
    ...rest
}) => {
    if ((desktopMinHeight && desktopMinHeight > 590) || maximizeDesktopHeight) {
        if (!maximizeDesktopHeight) {
            console.warn('Modal desktop heights cannot be greater than 590');
        }
        desktopMinHeight = 590;
    }

    const { isAboveBreakpoint } = useBreakpoints();
    const classes = useStyles({ desktopMaxHeight, desktopMinHeight, desktopMaxWidth, isAboveMd: isAboveBreakpoint('md') });

    const titleStyles: React.CSSProperties = {};
    if (centerTitle){
        titleStyles.textAlign = 'center';
        titleStyles.marginLeft = (backHandler ? undefined : 52);
        titleStyles.marginRight = (!hideCloseButton ? undefined : 52);
    }

    return (
        <IonModal
            {...rest} // do it this way so excludeAutoDismiss gets added without IDE complaining
            data-id={dataId}
            data-type={dataType}
            isOpen={isOpen}
            className={classes.modal}
            backdropDismiss={!preventBackdropDismiss}
            keepContentsMounted={keepContentsMounted}
            onWillPresent={() => {
                onWillPresent();
            }}
            onDidPresent={onDidPresent}
            onWillDismiss={(e: any) => {
                // invoke closeHandler, but only if the user clicked on the backdrop (or hit the escape key)
                // (this avoids invoking handler twice when close button clicked, or modal closed by consumer)
                if (e.detail.role === 'backdrop') {
                    closeHandler();
                }
            }}
        >
            <IonHeader translucent className={classes.modalHeader}>
                <IonToolbar color={titleColor}>
                    <IonTitle style={titleStyles}>{title}</IonTitle>
                    <IonButtons slot="start">
                        {backHandler && (
                            <MenuBackButton onClick={backHandler} color={buttonColor} />
                        )}
                    </IonButtons>
                    <IonButtons slot="end" style={{ marginRight: 4 }}>
                        {minimizeHandler && (
                            <IonButton
                                onClick={minimizeHandler}
                                color={buttonColor}
                                shape="round"
                                data-id="modal-minimize-button"
                                style={{ '--padding-start': '12px', '--padding-end': '12px', minHeight: 42, marginRight: -4 }}
                            >
                                <span style={{ position: 'relative', top: 3 }}>
                                    <MinusIcon color="light" />
                                </span>
                            </IonButton>
                        )}
                        {!hideCloseButton && (
                            <MenuCloseButton onClick={closeHandler} color={buttonColor} />
                        )}
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            {children}
        </IonModal>
    );
};
